import { useEffect, useState } from 'react';

export const THEMES = {
  dark: 'dark',
  light: 'light',
} as const;
export type Theme = (typeof THEMES)[keyof typeof THEMES];

export const THEME_STORAGE_KEY = 'meetblast.theme';
function isTheme(value: string | null): value is Theme {
  return value === THEMES.dark || value === THEMES.light;
}

function getSystemTheme(): Theme {
  if (typeof window === 'undefined') return THEMES.dark;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.dark : THEMES.light;
}

export function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle(THEMES.dark, theme === THEMES.dark);
  root.classList.toggle(THEMES.light, theme === THEMES.light);
}

export function useTheme(): { theme: Theme; toggle: () => void; setTheme: (t: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme() ?? getSystemTheme());

  useEffect(() => {
    applyTheme(theme);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // ignore quota / private mode
    }
  }, [theme]);

  useEffect(() => {
    if (readStoredTheme() !== null) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) =>
      setThemeState(e.matches ? THEMES.dark : THEMES.light);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return {
    theme,
    setTheme: setThemeState,
    toggle: () =>
      setThemeState((prev) => (prev === THEMES.dark ? THEMES.light : THEMES.dark)),
  };
}
